import { reactive , ref } from 'vue'
import { isAxiosError } from 'axios'
import { useRouter } from 'vue-router'
import { INDEX_CATEGORIES, INDEX_CATEGORY_MAP, Tag } from '@/js/pages/theindex/data'
import { ITheIndexPost } from '@/js/pages/theindex/types'
import type { IRogue } from '@/js/services/RogueTypes'

export const BASE_FIELDS = ['title', 'featuredImagePath', 'categories', 'id', 'tags', 'link', 'slug', 'format']
export const ALL_FIELDS = BASE_FIELDS.concat(['largeFeaturedImagePath', 'featuredVideo', 'content', 'excerpt', 'customCss', 'customJs', 'featured_media', 'date', 'modified'])

type TagOptions = {
  tags: number[];
};

type CategoryOptions = {
  categories: number[];
};

type TagFilterOption = {
  label: string;
  slug: string;
  value: number;
  sort: number;
};

let tagLoad: Promise<Tag[]>
const tags: { [key: number]: string } = {}

export const INDEX_FILTERS: { [key: string]: { label: string, slug: string, value: number, sort: number }[] } = reactive({})
INDEX_CATEGORIES.forEach(cat => {
  if (cat.slug) {
    INDEX_FILTERS[cat.slug] = []
  }
})

export async function loadTags(rogue: IRogue) {
  if (!tagLoad) {
    const url = `${rogue.theIndexBaseURL}/theindex/wp-json/wp/v2/tags?per_page=100&page=1&_fields=slug,id,count,description,name&hide_empty=true`
    tagLoad = rogue.api.fetch<Tag[]>({
      url,
      cache: { key: 'index-tag-load', seconds: 60 * 60 * 24 },
    })
      .then((tagResp: Tag[]) => {
        tagResp.forEach(tag => {
          tags[tag.id] = tag.name
          if (tag.description && INDEX_FILTERS[tag.description]) {
            INDEX_FILTERS[tag.description].push({ label: tag.name, value: tag.id, sort: tag.count, slug: tag.slug })
            INDEX_FILTERS[tag.description].sort((a, b) => b.sort - a.sort)
          }
        })

        return tagResp
      })
      .catch((err: Error) => {
        rogue.log.info('Error loading index tags', err)

        return []
      })
  }

  await Promise.resolve(tagLoad)
}


export async function loadTagsByQuery(rogue: IRogue, search: string, cat: number, catFilters: TagFilterOption[]) {

  const uniqueTagIds = new Set<number>()
  let currentPage = 1

  // Pagination Loop to Collect All Unique Tag IDs
  while (currentPage <= 2) {
    const url = `${rogue.theIndexBaseURL}/theindex/wp-json/wp/v2/posts?type=post&per_page=100&categories=${cat}&search=${search}&_fields=tags&page=${currentPage}`

    try {
      const tagOptionLoad: TagOptions[] = await rogue.api.fetch({
        url,
        cache: { key: `index-tag-post-page-${currentPage}-${cat}-${search}`, seconds: 60 * 60 * 24 },
      })

      if (!tagOptionLoad || tagOptionLoad.length === 0) {
        break 
      }

      if (currentPage === 2 && tagOptionLoad.length === 100) {
        return catFilters
      }

      tagOptionLoad.forEach(post => {
        if (Array.isArray(post.tags)) {
          post.tags.forEach(tagId => {
            if (tagId) uniqueTagIds.add(tagId)
          })
        }
      })
      
      if (tagOptionLoad.length < 100) {
        break
      }
      
      currentPage++ 
    } catch (err: any) {
      rogue.log.info('Error loading index tags during post pagination', err)
        
      return catFilters
    }
  }

  if (uniqueTagIds.size === 0) {
    return []
  }

  const filteredTags = catFilters?.filter((tag: TagFilterOption) => 
    uniqueTagIds.has(tag.value)
  )

  return filteredTags
  
}

export async function loadCategoriesByQuery(rogue: IRogue, search: string) {

  const uniqueCatIds = new Set<number>()
  let currentPage = 1

  while (currentPage <= 2) {
    const url = `${rogue.theIndexBaseURL}/theindex/wp-json/wp/v2/posts?type=post&per_page=100&search=${search}&_fields=categories&page=${currentPage}`

    try {
      const categoryOptionLoad: CategoryOptions[] = await rogue.api.fetch({
        url,
        cache: { key: `index-category-post-page-${currentPage}-${search}`, seconds: 60 * 60 * 24 },
      })
      
      if (!categoryOptionLoad || categoryOptionLoad.length === 0) {
        break
      }

      if (currentPage === 2 && categoryOptionLoad.length === 100) {
        return INDEX_CATEGORIES
      }

      categoryOptionLoad.forEach(post => {
        if (Array.isArray(post.categories)) {
          post.categories.forEach(catId => {
            if (catId) uniqueCatIds.add(catId)
          })
        }
      })
      
      if (categoryOptionLoad.length < 100) {
        break 
      }
      
      currentPage++
    } catch (err: any) {
      rogue.log.info('Error loading index categories during post pagination', err)
      
      return INDEX_CATEGORIES
    }
  }

  if (uniqueCatIds.size === 0) {
    return []
  }

  const filteredCategories = INDEX_CATEGORIES?.filter((cat: any) => 
    uniqueCatIds.has(cat.id)
  )

  return filteredCategories
}


function processPost(post: ITheIndexPost): void {
  if (!post) {
    return
  }
  const cat = post.categories
    .map(id => INDEX_CATEGORY_MAP[id])
    .filter(c => c?.slug)[0]
  if (cat && cat.slug) {
    post.category = { id: cat.id, name: cat.name, slug: cat.slug }
    if (post.slug) {
      post.link = `/theindex/${cat.slug}/${post.slug}`
    }
  } else {
    post.link = post.link.replace(/(.+)(\/theindex.*)/, '$2')
    post.category = { id: post.categories[0], name: 'Uncategorized', slug: '' }
  }
  if (post.category.id === 330 && post.tags.includes(775)) {
    post.category.name = 'Journeyman'
  }
  
}

export async function loadPosts(
  rogue: IRogue,
  query: Record<string, string | number> = {},
  { page = 1, perPage = 20, fields = BASE_FIELDS, embed = '', withHeaders = false } = {}
): Promise<ITheIndexPost[] & { totalCount?: number }> {

  if (fields?.length) {
    query._fields = fields.join(',')
  }
  if (embed?.length) {
    query._embed = fields.join(',')
  }
  if(query.search && typeof query.search !== 'number') {
    query.search = query.search.replace(/[^a-zA-Z0-9\s]/g, '')
  }
  const url = `${rogue.theIndexBaseURL}/theindex/wp-json/wp/v2/posts?type=post&per_page=${perPage}&page=${page}&${Object.keys(query).map(key => `${key}=${query[key]}`).join('&')}`

  const cacheKey = page === 1 && !query.search && perPage > 1 ? `index-posts-${JSON.stringify(query)}-${perPage}` : undefined

  let posts: string | ITheIndexPost[] = []
  let totalCount = 0

  try {
    const apiPromise = rogue.api.fetch<string | ITheIndexPost[]>({
      url,
      cache: cacheKey ? { key: cacheKey, seconds: 60 * 60 * 6 } : undefined,
    })
    const headPromise = withHeaders ? fetch(url, { method: 'HEAD' }) : Promise.resolve(null)
    const [apiResponse, headRes] = await Promise.all([apiPromise, headPromise])

    posts = apiResponse

    if (headRes) {
      totalCount = parseInt(headRes.headers.get('X-WP-Total') || '0', 10)
    }
   
  } catch (e) {
    if (isAxiosError(e)) {
      rogue.log.warn('Error loading index posts', e.response?.data || e.message)
    } else {
      rogue.log.warn('Error loading index posts', e as Error)
    }
  }
  if (typeof posts === 'string' && posts.indexOf('<pre style="') !== -1) {
    try {
      posts = JSON.parse(posts.replace(/(<([^>]+)>)/gi, '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&apos;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/&x27;/g, "'")
        .replace(/&#47;/g, '/')
        .replace(/&39;/g, '\\')) as ITheIndexPost[]
      console.error(`Got an HTML string from posts, parsed out ${posts.length}...`)
    } catch (e) {
      //
    }
  }

  if (Array.isArray(posts)) {
    posts.forEach(post => processPost(post))

    if (withHeaders) {
      (posts as any).totalCount = totalCount
    }

    return posts
  }

  return []
}



export async function getPostBySlug(
  rogue: IRogue, slug: string
): Promise<ITheIndexPost> {
  const post = (await loadPosts(rogue, { slug }, { fields: [], embed: 'author', perPage: 1 }))[0]
  processPost(post)

  return post
}

export function useIndexSearch() {
  const router = useRouter()
  const query = ref('')
  const selectedCategory = ref<number | null>(null)
  const searchFor = ref<string>('')

  function search(next?: string) {
    searchFor.value = next ?? query.value
    router.replace({
      query: {
        q   : searchFor.value || undefined,
        cat : selectedCategory.value || undefined,
      },
    })
  }

  function clear() {
    query.value = ''
    searchFor.value = ''
    router.replace({ query: {} })
  }

  return { query, selectedCategory, searchFor, search, clear }
}
